import React, { useState, useEffect } from 'react';
import { api, ApiError } from './api';

const fmtPeso = (n) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(n || 0);

const LOTE_VACIO = { numero_lote: '', cantidad: '', fecha_elaboracion: '', fecha_vencimiento: '' };
const ITEM_VACIO = { producto_id: null, descripcion: '', cantidad: '', costo_unitario: '', iva_porcentaje: 21, subtotal: 0, costo_anterior: 0, precio_anterior: 0, precio_sugerido: 0, actualizar_costo: true, actualizar_precio: false, maneja_lotes: false, lotes: [] };

const calcularSugerido = (costo, producto) => {
  const c = Number.parseFloat(costo) || 0;
  const m = Number.parseFloat(producto?.margen_valor_efectivo) || 0;
  const tipo = producto?.margen_tipo_efectivo || 'porcentaje';
  const redondeo = Number.parseFloat(producto?.redondeo_efectivo) || 0;
  let precio = tipo === 'monto' ? c + m : c * (1 + m / 100);
  if (redondeo > 0) precio = Math.ceil((precio - 1e-9) / redondeo) * redondeo;
  return Number(precio.toFixed(2));
};

export default function Compras() {
  const [pestaña, setPestaña] = useState('nueva');
  const [proveedores, setProveedores] = useState([]);
  const [productos, setProductos] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [facturaDetalle, setFacturaDetalle] = useState(null);

  // Formulario de nueva factura
  const [form, setForm] = useState({
    proveedor_id: '',
    tipo_comprobante: 'A',
    punto_venta: '',
    numero_comprobante: '',
    fecha_emision: new Date().toISOString().split('T')[0],
    fecha_vencimiento: '',
    condicion_pago: 'Cuenta corriente',
    importe_otros_tributos: '',
    actualizo_stock: true,
    afecto_caja: false,
    metodo_pago_caja: 'Efectivo',
    observaciones: '',
  });
  const [items, setItems] = useState([{ ...ITEM_VACIO }]);
  const [guardando, setGuardando] = useState(false);

  // Filtros del historial
  const hoy = new Date().toISOString().split('T')[0];
  const primerDiaMes = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
  const [filtros, setFiltros] = useState({ proveedor_id: '', estado: '', desde: primerDiaMes, hasta: hoy });

  useEffect(() => {
    Promise.all([api.get('/api/proveedores'), api.get('/api/precios/productos')])
      .then(([p, pr]) => {
        setProveedores(Array.isArray(p) ? p : []);
        setProductos(Array.isArray(pr) ? pr : []);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (pestaña === 'historial') cargarHistorial();
  }, [pestaña]); // eslint-disable-line react-hooks/exhaustive-deps

  const cargarHistorial = async () => {
    try {
      let path = `/api/facturas-compra?desde=${filtros.desde}&hasta=${filtros.hasta}`;
      if (filtros.proveedor_id) path += `&proveedor_id=${filtros.proveedor_id}`;
      if (filtros.estado) path += `&estado=${filtros.estado}`;
      const data = await api.get(path);
      setFacturas(Array.isArray(data) ? data : []);
    } catch (err) {
      alert("❌ " + (err instanceof ApiError ? err.mensaje : 'Error al cargar historial.'));
    }
  };

  const verDetalle = async (id) => {
    try {
      const data = await api.get(`/api/facturas-compra/${id}`);
      setFacturaDetalle(data);
    } catch (err) {
      alert("❌ " + (err instanceof ApiError ? err.mensaje : 'Error al cargar detalle.'));
    }
  };

  const anularFactura = async (id) => {
    if (!window.confirm('¿Anular esta factura? Se revertirá el stock, la deuda y el impacto en caja si los hubo.')) return;
    try {
      await api.post(`/api/facturas-compra/${id}/anular`);
      alert('✅ Factura anulada correctamente.');
      setFacturaDetalle(null);
      cargarHistorial();
    } catch (err) {
      alert("❌ " + (err instanceof ApiError ? err.mensaje : 'No se pudo anular.'));
    }
  };

  // ---- Manejo de ítems ----
  const agregarItem = () => setItems([...items, { ...ITEM_VACIO }]);

  const quitarItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const cambiarItem = (i, campo, valor) => {
    const copia = items.map((it, idx) => {
      if (idx !== i) return it;
      const nuevo = { ...it, [campo]: valor };
      if (campo === 'actualizar_costo' && !valor) nuevo.actualizar_precio = false;
      if (campo === 'actualizar_precio' && valor) nuevo.actualizar_costo = true;
      if (campo === 'producto_id' && valor) {
        const prod = productos.find((p) => p.id === parseInt(valor, 10));
        if (prod) {
          nuevo.descripcion = prod.descripcion || prod.nombre || '';
          nuevo.costo_unitario = prod.costo || prod.precio_costo || '';
          nuevo.costo_anterior = Number(prod.costo || prod.precio_costo || 0);
          nuevo.precio_anterior = Number(prod.precio_venta || 0);
          nuevo.precio_sugerido = calcularSugerido(nuevo.costo_unitario, prod);
          nuevo.actualizar_costo = true;
          nuevo.actualizar_precio = false;
          nuevo.maneja_lotes = Boolean(prod.maneja_lotes);
          nuevo.lotes = prod.maneja_lotes
            ? [{ ...LOTE_VACIO, cantidad: nuevo.cantidad || '' }]
            : [];
        }
      }
      if (campo === 'producto_id' && !valor) {
        nuevo.maneja_lotes = false;
        nuevo.lotes = [];
      }
      const cant = parseFloat(nuevo.cantidad) || 0;
      if (campo === 'cantidad' && nuevo.maneja_lotes && nuevo.lotes.length === 1) {
        nuevo.lotes = [{ ...nuevo.lotes[0], cantidad: valor }];
      }
      const costo = parseFloat(nuevo.costo_unitario) || 0;
      nuevo.subtotal = parseFloat((cant * costo).toFixed(2));
      if (nuevo.producto_id) {
        const prod = productos.find((p) => Number(p.id) === Number(nuevo.producto_id));
        nuevo.precio_sugerido = calcularSugerido(costo, prod);
      }
      return nuevo;
    });
    setItems(copia);
  };

  const cambiarLote = (itemIndex, loteIndex, campo, valor) => {
    setItems(items.map((item, i) => i !== itemIndex ? item : {
      ...item,
      lotes: item.lotes.map((lote, j) => j === loteIndex ? { ...lote, [campo]: valor } : lote),
    }));
  };

  const agregarLote = (itemIndex) => {
    setItems(items.map((item, i) => i !== itemIndex ? item : {
      ...item,
      lotes: [...item.lotes, { ...LOTE_VACIO }],
    }));
  };

  const quitarLote = (itemIndex, loteIndex) => {
    setItems(items.map((item, i) => i !== itemIndex ? item : {
      ...item,
      lotes: item.lotes.filter((_, j) => j !== loteIndex),
    }));
  };

  // ---- Totales calculados en el cliente ----
  const totales = (() => {
    const tipo = form.tipo_comprobante.toUpperCase();
    let neto = 0, iva = 0;
    for (const it of items) {
      const sub = parseFloat(it.cantidad || 0) * parseFloat(it.costo_unitario || 0);
      if (tipo === 'A') {
        neto += sub;
        iva += sub * ((parseFloat(it.iva_porcentaje) || 21) / 100);
      } else {
        neto += sub;
      }
    }
    const otros = parseFloat(form.importe_otros_tributos) || 0;
    neto = parseFloat(neto.toFixed(2));
    iva = parseFloat(iva.toFixed(2));
    const total = parseFloat((neto + iva + otros).toFixed(2));
    return { neto, iva, otros, total };
  })();

  const guardarFactura = async () => {
    if (!form.proveedor_id) return alert('Seleccioná un proveedor.');
    if (!form.fecha_emision) return alert('Indicá la fecha de la factura.');
    if (items.some((it) => !it.descripcion.trim() || !it.cantidad || !it.costo_unitario)) {
      return alert('Completá descripción, cantidad y costo en todos los ítems.');
    }
    if (form.actualizo_stock) {
      for (const item of items.filter((it) => it.producto_id && it.maneja_lotes)) {
        if (!item.lotes.length || item.lotes.some((lote) => !String(lote.numero_lote || '').trim() || !(Number(lote.cantidad) > 0))) {
          return alert(`Completá número y cantidad de todos los lotes de ${item.descripcion}.`);
        }
        const suma = item.lotes.reduce((total, lote) => total + Number(lote.cantidad || 0), 0);
        if (Math.abs(suma - Number(item.cantidad || 0)) > 0.0001) {
          return alert(`La suma de lotes de ${item.descripcion} (${suma}) debe coincidir con la cantidad comprada (${item.cantidad}).`);
        }
      }
    }
    setGuardando(true);
    try {
      const data = await api.post('/api/facturas-compra', {
        ...form,
        items: items.map((it) => ({
          producto_id: it.producto_id ? parseInt(it.producto_id, 10) : null,
          descripcion: it.descripcion.trim(),
          cantidad: parseFloat(it.cantidad),
          costo_unitario: parseFloat(it.costo_unitario),
          iva_porcentaje: parseFloat(it.iva_porcentaje) || 21,
          actualizar_costo: Boolean(it.actualizar_costo),
          actualizar_precio: Boolean(it.actualizar_precio),
          lotes: form.actualizo_stock && it.maneja_lotes
            ? it.lotes.map((lote) => ({
                numero_lote: String(lote.numero_lote || '').trim(),
                cantidad: parseFloat(lote.cantidad),
                fecha_elaboracion: lote.fecha_elaboracion || null,
                fecha_vencimiento: lote.fecha_vencimiento || null,
              }))
            : [],
        })),
      });
      alert(`✅ Factura #${data.factura_id} registrada. Total: ${fmtPeso(data.importe_total)}`);
      setForm({
        proveedor_id: '', tipo_comprobante: 'A', punto_venta: '', numero_comprobante: '',
        fecha_emision: new Date().toISOString().split('T')[0],
    fecha_vencimiento: '',
    condicion_pago: 'Cuenta corriente',
        importe_otros_tributos: '', actualizo_stock: true, afecto_caja: false,
        metodo_pago_caja: 'Efectivo', observaciones: '',
      });
      setItems([{ ...ITEM_VACIO }]);
    } catch (err) {
      alert("❌ " + (err instanceof ApiError ? err.mensaje : 'Error al guardar.'));
    } finally {
      setGuardando(false);
    }
  };

  const tabBtn = (key, label) => (
    <button
      onClick={() => setPestaña(key)}
      style={{
        padding: '10px 24px', border: 'none', cursor: 'pointer', fontWeight: 'bold',
        borderBottom: pestaña === key ? '3px solid #8b5cf6' : '3px solid transparent',
        backgroundColor: 'transparent', color: pestaña === key ? '#8b5cf6' : '#64748b',
        fontSize: '14px',
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', minHeight: '80vh' }}>
      <h2 style={{ color: '#0f172a', margin: '0 0 4px 0' }}>🛒 Compras / Facturas de Proveedor</h2>
      <p style={{ color: '#64748b', margin: '0 0 20px 0', fontSize: '14px' }}>Registrá las facturas que recibís de tus proveedores. Afectan el stock y la cuenta corriente automáticamente.</p>

      <div style={{ borderBottom: '1px solid #e2e8f0', marginBottom: '24px' }}>
        {tabBtn('nueva', '➕ Nueva Factura')}
        {tabBtn('historial', '📋 Historial')}
      </div>

      {/* ======================== NUEVA FACTURA ======================== */}
      {pestaña === 'nueva' && (
        <div style={{ maxWidth: '900px' }}>

          {/* Cabecera */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '13px' }}>Proveedor *</label>
              <select value={form.proveedor_id} onChange={e => setForm({ ...form, proveedor_id: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #cbd5e1' }}>
                <option value=''>— Seleccioná un proveedor —</option>
                {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '13px' }}>Tipo de Comprobante *</label>
              <select value={form.tipo_comprobante} onChange={e => setForm({ ...form, tipo_comprobante: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #cbd5e1' }}>
                <option value='A'>Factura A (con IVA discriminado)</option>
                <option value='B'>Factura B (IVA incluido)</option>
                <option value='C'>Factura C (Monotributista)</option>
                <option value='X'>Remito / Sin comprobante</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '13px' }}>Número de comprobante</label>
              <input type='text' placeholder='Ej: 00005-00012345'
                value={form.numero_comprobante} onChange={e => setForm({ ...form, numero_comprobante: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #cbd5e1' }} />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '13px' }}>Fecha de emisión *</label>
              <input type='date' value={form.fecha_emision} onChange={e => setForm({ ...form, fecha_emision: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #cbd5e1' }} />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '13px' }}>Vencimiento</label>
              <input type='date' value={form.fecha_vencimiento} onChange={e => setForm({ ...form, fecha_vencimiento: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #cbd5e1' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '13px' }}>Condición de pago</label>
              <select value={form.condicion_pago} onChange={e => setForm({ ...form, condicion_pago: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #cbd5e1' }}>
                <option>Cuenta corriente</option><option>Contado</option><option>7 días</option><option>15 días</option><option>30 días</option><option>60 días</option><option>90 días</option>
              </select>
            </div>
          </div>

          {/* Ítems */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3 style={{ margin: 0, color: '#374151', fontSize: '15px' }}>📦 Ítems de la factura</h3>
              <button onClick={agregarItem} style={{ backgroundColor: '#8b5cf6', color: 'white', padding: '7px 14px', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}>
                + Agregar ítem
              </button>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1180px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc' }}>
                    {['Producto (opcional)', 'Descripción *', 'Cantidad *', 'Costo anterior', 'Costo nuevo *', 'Precio actual', 'Sugerido', 'Actualizar', form.tipo_comprobante === 'A' ? '% IVA' : '', 'Subtotal', ''].map((h, i) =>
                      h ? <th key={i} style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', fontSize: '12px', color: '#64748b' }}>{h}</th> : null
                    )}
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '6px' }}>
                        <select value={it.producto_id || ''} onChange={e => cambiarItem(i, 'producto_id', e.target.value || null)}
                          style={{ padding: '7px', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '12px', width: '100%' }}>
                          <option value=''>— Libre —</option>
                          {productos.map(p => <option key={p.id} value={p.id}>{p.descripcion}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '6px' }}>
                        <input value={it.descripcion} onChange={e => cambiarItem(i, 'descripcion', e.target.value)}
                          placeholder='Descripción' style={{ padding: '7px', border: '1px solid #e2e8f0', borderRadius: '4px', width: '100%', fontSize: '12px' }} />
                      </td>
                      <td style={{ padding: '6px', width: '80px' }}>
                        <input type='number' min='0' value={it.cantidad} onChange={e => cambiarItem(i, 'cantidad', e.target.value)}
                          style={{ padding: '7px', border: '1px solid #e2e8f0', borderRadius: '4px', width: '100%', fontSize: '12px', textAlign: 'right' }} />
                      </td>
                      <td style={{ padding: '6px', textAlign: 'right', fontSize: '12px', color: '#64748b' }}>${Number(it.costo_anterior || 0).toFixed(2)}</td>
                      <td style={{ padding: '6px', width: '110px' }}>
                        <input type='number' min='0' step='0.01' value={it.costo_unitario} onChange={e => cambiarItem(i, 'costo_unitario', e.target.value)}
                          style={{ padding: '7px', border: '1px solid #e2e8f0', borderRadius: '4px', width: '100%', fontSize: '12px', textAlign: 'right' }} />
                      </td>
                      <td style={{ padding: '6px', textAlign: 'right', fontSize: '12px' }}>${Number(it.precio_anterior || 0).toFixed(2)}</td>
                      <td style={{ padding: '6px', textAlign: 'right', fontWeight: 'bold', color: '#059669', fontSize: '12px' }}>${Number(it.precio_sugerido || 0).toFixed(2)}</td>
                      <td style={{ padding: '6px', minWidth: '145px', fontSize: '11px' }}>
                        {it.producto_id ? <>
                          <label style={{ display:'block' }}><input type='checkbox' checked={Boolean(it.actualizar_costo)} onChange={e=>cambiarItem(i,'actualizar_costo',e.target.checked)} /> Costo</label>
                          <label style={{ display:'block', marginTop:3 }}><input type='checkbox' checked={Boolean(it.actualizar_precio)} onChange={e=>cambiarItem(i,'actualizar_precio',e.target.checked)} /> Precio sugerido</label>
                        </> : <span style={{color:'#94a3b8'}}>Ítem libre</span>}
                      </td>
                      {form.tipo_comprobante === 'A' && (
                        <td style={{ padding: '6px', width: '80px' }}>
                          <select value={it.iva_porcentaje} onChange={e => cambiarItem(i, 'iva_porcentaje', e.target.value)}
                            style={{ padding: '7px', border: '1px solid #e2e8f0', borderRadius: '4px', width: '100%', fontSize: '12px' }}>
                            <option value={21}>21%</option>
                            <option value={10.5}>10.5%</option>
                            <option value={0}>Exento</option>
                          </select>
                        </td>
                      )}
                      <td style={{ padding: '6px', textAlign: 'right', fontWeight: 'bold', color: '#374151', fontSize: '13px' }}>
                        {fmtPeso(it.subtotal)}
                      </td>
                      <td style={{ padding: '6px', textAlign: 'center' }}>
                        {items.length > 1 && (
                          <button onClick={() => quitarItem(i)} style={{ backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}>✕</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {form.actualizo_stock && items.map((item, itemIndex) => item.producto_id && item.maneja_lotes ? (
              <div key={`lotes-${itemIndex}`} style={{ marginTop: '12px', padding: '14px', border: '1px solid #bfdbfe', borderRadius: '8px', backgroundColor: '#eff6ff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                  <div>
                    <strong style={{ color: '#1e3a8a' }}>🧾 Lotes de {item.descripcion || `ítem ${itemIndex + 1}`}</strong>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>La suma debe coincidir con {item.cantidad || 0} unidades.</div>
                  </div>
                  <button type='button' onClick={() => agregarLote(itemIndex)} style={{ border: 'none', borderRadius: '6px', padding: '7px 11px', backgroundColor: '#2563eb', color: 'white', fontWeight: 'bold', cursor: 'pointer' }}>+ Otro lote</button>
                </div>
                {item.lotes.map((lote, loteIndex) => (
                  <div key={loteIndex} style={{ display: 'grid', gridTemplateColumns: '1.3fr .7fr 1fr 1fr auto', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                    <input value={lote.numero_lote} onChange={e => cambiarLote(itemIndex, loteIndex, 'numero_lote', e.target.value)} placeholder='Número de lote *' style={{ padding: '8px', border: '1px solid #93c5fd', borderRadius: '5px' }} />
                    <input type='number' min='0.001' step='0.001' value={lote.cantidad} onChange={e => cambiarLote(itemIndex, loteIndex, 'cantidad', e.target.value)} placeholder='Cantidad *' style={{ padding: '8px', border: '1px solid #93c5fd', borderRadius: '5px' }} />
                    <div><label style={{ display: 'block', fontSize: '10px', color: '#64748b' }}>Elaboración</label><input type='date' value={lote.fecha_elaboracion} onChange={e => cambiarLote(itemIndex, loteIndex, 'fecha_elaboracion', e.target.value)} style={{ width: '100%', padding: '7px', border: '1px solid #93c5fd', borderRadius: '5px' }} /></div>
                    <div><label style={{ display: 'block', fontSize: '10px', color: '#64748b' }}>Vencimiento</label><input type='date' value={lote.fecha_vencimiento} onChange={e => cambiarLote(itemIndex, loteIndex, 'fecha_vencimiento', e.target.value)} style={{ width: '100%', padding: '7px', border: '1px solid #93c5fd', borderRadius: '5px' }} /></div>
                    <button type='button' onClick={() => quitarLote(itemIndex, loteIndex)} disabled={item.lotes.length === 1} style={{ border: '1px solid #fecaca', borderRadius: '5px', padding: '7px 9px', backgroundColor: '#fff', color: '#dc2626', cursor: item.lotes.length === 1 ? 'not-allowed' : 'pointer' }}>✕</button>
                  </div>
                ))}
              </div>
            ) : null)}
          </div>

          {/* Opciones y totales */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '20px' }}>
            <div>
              <h3 style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151', marginBottom: '12px' }}>⚙️ Opciones</h3>

              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', cursor: 'pointer' }}>
                <input type='checkbox' checked={form.actualizo_stock} onChange={e => setForm({ ...form, actualizo_stock: e.target.checked })} />
                <span style={{ fontSize: '14px' }}>Sumar al stock automáticamente</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', cursor: 'pointer' }}>
                <input type='checkbox' checked={form.afecto_caja} onChange={e => setForm({ ...form, afecto_caja: e.target.checked })} />
                <span style={{ fontSize: '14px' }}>Pagué la factura al cargarla</span>
              </label>

              {form.afecto_caja && (
                <div style={{ marginLeft: '28px' }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '12px', color: '#64748b' }}>Medio de pago</label>
                  <select value={form.metodo_pago_caja} onChange={e => setForm({ ...form, metodo_pago_caja: e.target.value })}
                    style={{ padding: '8px 12px', borderRadius: '5px', border: '1px solid #cbd5e1', fontSize: '13px' }}>
                    <option>Efectivo</option>
                    <option>Transferencia</option>
                    <option>Mercado Pago</option>
                    <option>Tarjeta</option>
                    <option>Cheque</option>
                  </select>
                </div>
              )}

              <div style={{ marginTop: '12px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '12px', color: '#64748b' }}>Observaciones</label>
                <textarea value={form.observaciones} onChange={e => setForm({ ...form, observaciones: e.target.value })}
                  rows={2} placeholder='Ej: Pedido semanal de bebidas, condiciones de pago...'
                  style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #cbd5e1', fontSize: '13px', resize: 'vertical' }} />
              </div>
            </div>

            {/* Totalizador */}
            <div style={{ backgroundColor: '#f8fafc', padding: '20px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <h3 style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151', marginBottom: '16px' }}>💰 Resumen de la factura</h3>
              {form.tipo_comprobante === 'A' ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                    <span style={{ color: '#64748b' }}>Neto gravado:</span>
                    <span style={{ fontWeight: 'bold' }}>{fmtPeso(totales.neto)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                    <span style={{ color: '#64748b' }}>IVA (21%):</span>
                    <span style={{ fontWeight: 'bold' }}>{fmtPeso(totales.iva)}</span>
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                  <span style={{ color: '#64748b' }}>Subtotal:</span>
                  <span style={{ fontWeight: 'bold' }}>{fmtPeso(totales.neto)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                <span style={{ color: '#64748b' }}>Otros tributos:</span>
                <input type='number' min='0' value={form.importe_otros_tributos}
                  onChange={e => setForm({ ...form, importe_otros_tributos: e.target.value })}
                  placeholder='0'
                  style={{ width: '110px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', textAlign: 'right', fontSize: '14px' }} />
              </div>
              <div style={{ borderTop: '2px solid #e2e8f0', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', fontSize: '18px' }}>
                <span style={{ fontWeight: 'bold' }}>TOTAL:</span>
                <span style={{ fontWeight: 'bold', color: '#8b5cf6' }}>{fmtPeso(totales.total)}</span>
              </div>

              {form.afecto_caja && (
                <div style={{ marginTop: '12px', padding: '8px 12px', backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '6px', fontSize: '13px', color: '#c2410c' }}>
                  {form.metodo_pago_caja === 'Efectivo'
                    ? `⚠️ Se registrará un egreso de caja de ${fmtPeso(totales.total)}.`
                    : `ℹ️ Se registrará como pagada por ${form.metodo_pago_caja}. No modificará el efectivo físico.`}
                </div>
              )}

              <button onClick={guardarFactura} disabled={guardando}
                style={{ width: '100%', marginTop: '16px', padding: '14px', backgroundColor: guardando ? '#a78bfa' : '#8b5cf6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '16px', cursor: guardando ? 'default' : 'pointer' }}>
                {guardando ? '⏳ Guardando...' : '✅ Confirmar Factura de Compra'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================== HISTORIAL ======================== */}
      {pestaña === 'historial' && (
        <div>
          {/* Filtros */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'flex-end' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Desde</label>
              <input type='date' value={filtros.desde} onChange={e => setFiltros({ ...filtros, desde: e.target.value })}
                style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '5px' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Hasta</label>
              <input type='date' value={filtros.hasta} onChange={e => setFiltros({ ...filtros, hasta: e.target.value })}
                style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '5px' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Proveedor</label>
              <select value={filtros.proveedor_id} onChange={e => setFiltros({ ...filtros, proveedor_id: e.target.value })}
                style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '5px' }}>
                <option value=''>Todos</option>
                {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Estado</label>
              <select value={filtros.estado} onChange={e => setFiltros({ ...filtros, estado: e.target.value })}
                style={{ padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '5px' }}>
                <option value=''>Todos</option>
                <option value='Pendiente'>Pendiente</option>
                <option value='Pagada Parcialmente'>Pagada Parcialmente</option>
                <option value='Pagada'>Pagada</option>
                <option value='Anulada'>Anulada</option>
              </select>
            </div>
            <button onClick={cargarHistorial} style={{ padding: '8px 18px', backgroundColor: '#8b5cf6', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>
              🔍 Filtrar
            </button>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
              <thead style={{ backgroundColor: '#f8fafc' }}>
                <tr>
                  {['Fecha', 'Proveedor', 'Tipo', 'Número', 'Total', 'Saldo pend.', 'Estado', ''].map((h, i) =>
                    <th key={i} style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontSize: '12px', color: '#64748b' }}>{h}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {facturas.map(f => {
                  const estadoColor = f.estado === 'Anulada' ? '#64748b' : f.estado === 'Pagada' ? '#16a34a' : f.estado === 'Pagada Parcialmente' ? '#d97706' : '#dc2626';
                  return (
                    <tr key={f.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 12px', fontSize: '13px' }}>{new Date(f.fecha_emision).toLocaleDateString('es-AR')}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 'bold', fontSize: '13px' }}>{f.proveedor_nombre}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontWeight: 'bold', color: '#8b5cf6', backgroundColor: '#f5f3ff', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                          FCO {f.tipo_comprobante}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', color: '#64748b', fontSize: '13px' }}>{f.numero_comprobante || '—'}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 'bold', fontSize: '13px' }}>{fmtPeso(f.importe_total)}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 'bold', fontSize: '13px', color: parseFloat(f.saldo_pendiente) > 0 ? '#dc2626' : '#16a34a' }}>
                        {fmtPeso(f.saldo_pendiente)}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 'bold', color: estadoColor }}>{f.estado}</span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <button onClick={() => verDetalle(f.id)} style={{ backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: '4px', padding: '5px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                          Ver
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {facturas.length === 0 && (
                  <tr><td colSpan='8' style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>No se encontraron facturas en el período seleccionado.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ======================== MODAL DETALLE ======================== */}
      {facturaDetalle && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '30px', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '10px', width: '90%', maxWidth: '700px', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div>
                <h3 style={{ margin: 0, color: '#0f172a' }}>
                  Factura {facturaDetalle.factura.tipo_comprobante} — {facturaDetalle.factura.numero_comprobante || `#${facturaDetalle.factura.id}`}
                </h3>
                <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13px' }}>
                  {facturaDetalle.factura.proveedor_nombre} • {new Date(facturaDetalle.factura.fecha_emision).toLocaleDateString('es-AR')} • {facturaDetalle.factura.condicion_pago || 'Sin condición'}{facturaDetalle.factura.fecha_vencimiento ? ` • vence ${new Date(facturaDetalle.factura.fecha_vencimiento).toLocaleDateString('es-AR')}` : ''}
                </p>
              </div>
              <button onClick={() => setFacturaDetalle(null)} style={{ backgroundColor: '#e2e8f0', border: 'none', borderRadius: '5px', padding: '6px 12px', cursor: 'pointer', fontWeight: 'bold' }}>✕</button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
              <thead style={{ backgroundColor: '#f8fafc' }}>
                <tr>
                  {['Descripción', 'Cantidad', 'Costo Unit.', 'Precio sugerido', 'Actualización', 'Subtotal'].map((h, i) =>
                    <th key={i} style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', fontSize: '12px', color: '#64748b' }}>{h}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {facturaDetalle.items.map((it, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 10px', fontSize: '13px' }}>{it.descripcion}</td>
                    <td style={{ padding: '8px 10px', fontSize: '13px', textAlign: 'right' }}>{it.cantidad}</td>
                    <td style={{ padding: '8px 10px', fontSize: '13px', textAlign: 'right' }}>{fmtPeso(it.costo_unitario)}</td>
                    <td style={{ padding: '8px 10px', fontSize: '13px', textAlign: 'right', color:'#059669' }}>{it.precio_sugerido == null ? '—' : fmtPeso(it.precio_sugerido)}</td>
                    <td style={{ padding: '8px 10px', fontSize: '12px' }}>{it.actualizo_costo ? 'Costo' : ''}{it.actualizo_costo && it.actualizo_precio ? ' + ' : ''}{it.actualizo_precio ? 'Precio' : ''}</td>
                    <td style={{ padding: '8px 10px', fontSize: '13px', textAlign: 'right', fontWeight: 'bold' }}>{fmtPeso(it.subtotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ backgroundColor: '#f8fafc', padding: '12px 16px', borderRadius: '6px', marginBottom: '16px' }}>
              {parseFloat(facturaDetalle.factura.importe_iva) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '13px' }}>
                  <span>Neto Gravado:</span><span>{fmtPeso(facturaDetalle.factura.importe_neto)}</span>
                </div>
              )}
              {parseFloat(facturaDetalle.factura.importe_iva) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '13px' }}>
                  <span>IVA:</span><span>{fmtPeso(facturaDetalle.factura.importe_iva)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '8px', marginTop: '4px' }}>
                <span>TOTAL:</span><span style={{ color: '#8b5cf6' }}>{fmtPeso(facturaDetalle.factura.importe_total)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '13px', fontWeight: 'bold', color: parseFloat(facturaDetalle.factura.saldo_pendiente) > 0 ? '#dc2626' : '#16a34a' }}>
                <span>Saldo pendiente:</span><span>{fmtPeso(facturaDetalle.factura.saldo_pendiente)}</span>
              </div>
            </div>

            {facturaDetalle.pagos.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#64748b' }}>Pagos realizados</h4>
                {facturaDetalle.pagos.map((p, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}>
                    <span>{new Date(p.fecha).toLocaleDateString('es-AR')} — {p.metodo_pago || 'Efectivo'}</span>
                    <span style={{ color: '#16a34a', fontWeight: 'bold' }}>{fmtPeso(p.monto)}</span>
                  </div>
                ))}
              </div>
            )}

            {facturaDetalle.factura.estado !== 'Anulada' && (
              <button onClick={() => anularFactura(facturaDetalle.factura.id)}
                style={{ width: '100%', padding: '10px', backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}>
                ⊘ Anular esta factura
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
